import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import playerProfileService from '../../services/playerProfileService';

const initialState = {
  profile: null,
  loading: false,
  error: null,
  updateLoading: false,
  videoLoading: false,
  recommendationLoading: false,
  profileImageLoading: false,
  academicInfoLoading: false,
  updateError: null,
  // Viewed Player Profile
  viewedPlayer: null,
  viewedPlayerLoading: false,
  viewedPlayerError: null,
  // Team Roster
  teamRoster: [],
  rosterPagination: {
    currentPage: 1,
    totalPages: 1,
    totalPlayers: 0,
    limit: 20,
    hasMore: false,
  },
  rosterLoading: false,
  rosterError: null,
};

export const getPlayerProfile = createAsyncThunk(
  'playerProfile/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.getProfile();
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch player profile.'
      );
    }
  }
);

export const updatePlayerProfile = createAsyncThunk(
  'playerProfile/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.updateProfile(profileData);
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update player profile.'
      );
    }
  }
);

// Video Actions
export const uploadVideo = createAsyncThunk(
  'playerProfile/uploadVideo',
  async (videoFile, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.uploadVideo(videoFile);
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to upload video.'
      );
    }
  }
);

export const deleteVideo = createAsyncThunk(
  'playerProfile/deleteVideo',
  async (videoId, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.deleteVideo(videoId);
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete video.'
      );
    }
  }
);

// Recommendation Actions
export const uploadRecommendation = createAsyncThunk(
  'playerProfile/uploadRecommendation',
  async (recommendationFile, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.uploadRecommendation(recommendationFile);
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to upload recommendation.'
      );
    }
  }
);

export const deleteRecommendation = createAsyncThunk(
  'playerProfile/deleteRecommendation',
  async (_, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.deleteRecommendation();
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete recommendation.'
      );
    }
  }
);

// Strengths Actions
export const addStrength = createAsyncThunk(
  'playerProfile/addStrength',
  async (strength, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.addStrength(strength);
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to add strength.'
      );
    }
  }
);

export const deleteStrength = createAsyncThunk(
  'playerProfile/deleteStrength',
  async (strength, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.deleteStrength(strength);
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete strength.'
      );
    }
  }
);

// Awards Actions
export const addAward = createAsyncThunk(
  'playerProfile/addAward',
  async (award, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.addAward(award);
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to add award.'
      );
    }
  }
);

export const deleteAward = createAsyncThunk(
  'playerProfile/deleteAward',
  async (award, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.deleteAward(award);
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete award.'
      );
    }
  }
);

// Profile Image Actions
export const updateProfileImage = createAsyncThunk(
  'playerProfile/updateProfileImage',
  async (imageFile, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.updateProfileImage(imageFile);
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update profile image.'
      );
    }
  }
);

export const deleteProfileImage = createAsyncThunk(
  'playerProfile/deleteProfileImage',
  async (_, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.deleteProfileImage();
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete profile image.'
      );
    }
  }
);

// Academic Info Actions
export const uploadAcademicInfo = createAsyncThunk(
  'playerProfile/uploadAcademicInfo',
  async (pdfFile, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.uploadAcademicInfo(pdfFile);
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to upload academic info.'
      );
    }
  }
);

export const deleteAcademicInfo = createAsyncThunk(
  'playerProfile/deleteAcademicInfo',
  async (_, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.deleteAcademicInfo();
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete academic info.'
      );
    }
  }
);

// Team Roster Action
export const fetchPlayerTeamRoster = createAsyncThunk(
  'playerProfile/fetchTeamRoster',
  async ({ teamId, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.getTeamRoster(teamId, page, limit);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch team roster.'
      );
    }
  }
);

// Get Player by ID Action
export const getPlayerById = createAsyncThunk(
  'playerProfile/getPlayerById',
  async (playerId, { rejectWithValue }) => {
    try {
      const data = await playerProfileService.getPlayerById(playerId);
      return data.player;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch player profile.'
      );
    }
  }
);

const playerProfileSlice = createSlice({
  name: 'playerProfile',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.updateError = null;
    },
    clearProfile: (state) => {
      state.profile = null;
      state.error = null;
      state.updateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Player Profile
      .addCase(getPlayerProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPlayerProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
        state.error = null;
      })
      .addCase(getPlayerProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Player Profile
      .addCase(updatePlayerProfile.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updatePlayerProfile.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(updatePlayerProfile.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })
      // Upload Video
      .addCase(uploadVideo.pending, (state) => {
        state.videoLoading = true;
        state.updateError = null;
      })
      .addCase(uploadVideo.fulfilled, (state, action) => {
        state.videoLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(uploadVideo.rejected, (state, action) => {
        state.videoLoading = false;
        state.updateError = action.payload;
      })
      // Delete Video
      .addCase(deleteVideo.pending, (state) => {
        state.videoLoading = true;
        state.updateError = null;
      })
      .addCase(deleteVideo.fulfilled, (state, action) => {
        state.videoLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(deleteVideo.rejected, (state, action) => {
        state.videoLoading = false;
        state.updateError = action.payload;
      })
      // Upload Recommendation
      .addCase(uploadRecommendation.pending, (state) => {
        state.recommendationLoading = true;
        state.updateError = null;
      })
      .addCase(uploadRecommendation.fulfilled, (state, action) => {
        state.recommendationLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(uploadRecommendation.rejected, (state, action) => {
        state.recommendationLoading = false;
        state.updateError = action.payload;
      })
      // Delete Recommendation
      .addCase(deleteRecommendation.pending, (state) => {
        state.recommendationLoading = true;
        state.updateError = null;
      })
      .addCase(deleteRecommendation.fulfilled, (state, action) => {
        state.recommendationLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(deleteRecommendation.rejected, (state, action) => {
        state.recommendationLoading = false;
        state.updateError = action.payload;
      })
      // Add Strength
      .addCase(addStrength.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(addStrength.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(addStrength.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })
      // Delete Strength
      .addCase(deleteStrength.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(deleteStrength.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(deleteStrength.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })
      // Add Award
      .addCase(addAward.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(addAward.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(addAward.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })
      // Delete Award
      .addCase(deleteAward.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(deleteAward.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(deleteAward.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload;
      })
      // Update Profile Image
      .addCase(updateProfileImage.pending, (state) => {
        state.profileImageLoading = true;
        state.updateError = null;
      })
      .addCase(updateProfileImage.fulfilled, (state, action) => {
        state.profileImageLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(updateProfileImage.rejected, (state, action) => {
        state.profileImageLoading = false;
        state.updateError = action.payload;
      })
      // Delete Profile Image
      .addCase(deleteProfileImage.pending, (state) => {
        state.profileImageLoading = true;
        state.updateError = null;
      })
      .addCase(deleteProfileImage.fulfilled, (state, action) => {
        state.profileImageLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(deleteProfileImage.rejected, (state, action) => {
        state.profileImageLoading = false;
        state.updateError = action.payload;
      })
      // Upload Academic Info
      .addCase(uploadAcademicInfo.pending, (state) => {
        state.academicInfoLoading = true;
        state.updateError = null;
      })
      .addCase(uploadAcademicInfo.fulfilled, (state, action) => {
        state.academicInfoLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(uploadAcademicInfo.rejected, (state, action) => {
        state.academicInfoLoading = false;
        state.updateError = action.payload;
      })
      // Delete Academic Info
      .addCase(deleteAcademicInfo.pending, (state) => {
        state.academicInfoLoading = true;
        state.updateError = null;
      })
      .addCase(deleteAcademicInfo.fulfilled, (state, action) => {
        state.academicInfoLoading = false;
        state.profile = action.payload;
        state.updateError = null;
      })
      .addCase(deleteAcademicInfo.rejected, (state, action) => {
        state.academicInfoLoading = false;
        state.updateError = action.payload;
      })
      // Fetch Team Roster
      .addCase(fetchPlayerTeamRoster.pending, (state) => {
        state.rosterLoading = true;
        state.rosterError = null;
      })
      .addCase(fetchPlayerTeamRoster.fulfilled, (state, action) => {
        state.rosterLoading = false;
        const newPlayers = action.payload.players || action.payload.roster || [];
        const currentPage = action.payload.pagination?.currentPage || action.payload.currentPage || 1;

        // If it's the first page, replace the roster; otherwise, append to it
        if (currentPage === 1) {
          state.teamRoster = newPlayers;
        } else {
          state.teamRoster = [...state.teamRoster, ...newPlayers];
        }

        state.rosterPagination = {
          currentPage: currentPage,
          totalPages: action.payload.pagination?.totalPages || action.payload.totalPages || 1,
          totalPlayers: action.payload.pagination?.totalCount || action.payload.totalPlayers || 0,
          limit: action.payload.pagination?.limit || action.payload.limit || 20,
          hasMore: action.payload.pagination?.hasMore || false,
        };
        state.rosterError = null;
      })
      .addCase(fetchPlayerTeamRoster.rejected, (state, action) => {
        state.rosterLoading = false;
        state.rosterError = action.payload;
      })
      // Get Player by ID
      .addCase(getPlayerById.pending, (state) => {
        state.viewedPlayerLoading = true;
        state.viewedPlayerError = null;
      })
      .addCase(getPlayerById.fulfilled, (state, action) => {
        state.viewedPlayerLoading = false;
        state.viewedPlayer = action.payload;
        state.viewedPlayerError = null;
      })
      .addCase(getPlayerById.rejected, (state, action) => {
        state.viewedPlayerLoading = false;
        state.viewedPlayerError = action.payload;
      });
  },
});

export const { clearError, clearProfile } = playerProfileSlice.actions;
export default playerProfileSlice.reducer;
